// App.js
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import Alert from "@mui/material/Alert";
import { SIDEBAR_MENU, GROUP_MATRIX, FEATURE_REGISTRY } from "./utils/accessRegistry";
import { getFeaturesForRoles, getSidebarForRoles, canAccessSidebarKey } from "./utils/accessUtil";
import { UserMenu } from "components/UserMenu";
import { LayoutPreferencesProvider } from "components/LayoutPreferencesProvider";
import logo from "./img/jma-logo.svg";
import KeycloakContext from "./KeycloakProvider";
import { useContext } from "react";
import Sidebar from "views/common/Sidebar";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReorderIcon from '@mui/icons-material/Reorder';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { Dashboard } from "views/Dashboard";
import { extractRealmRoles } from "utils/AppUtil";
import { useAddGroupMutation } from "api/homeApi";
import XranScreen from "views/Xran/XranScreen";
import ShiftsManagement from "views/ShiftsManagement";
import ProtectedRoute from "components/ProtectedRoute";
import LoginTracking from "views/LoginTracking";
import DataRequest from "views/DataRequest";
import OutageTracker from "views/OutageTracker";
import NocHardware from "views/NocHardware";
import AlarmReportsSu from "views/Reports/AlarmReportsSu";
import AlarmReportsAll from "views/Reports/AlarmReportsAll";
import AlarmReportsIntl from "views/Reports/AlarmReportsIntl";
import WindsorReports from "views/Reports/WindsorReports";
import TmobileReports from "views/Reports/TmobileReports";
import SiteReports from "views/Reports/SiteReports";
import KpiDashboardBeta from "views/Reports/KpiDashboardBeta";
import { XranTracking } from "views/XranTracking";
import ReleaseNotes from "views/ReleaseNotes";
import XranCerts from "views/XranCerts";
import CampusMap from "views/CampusMap";
import SUPerBuildingUserData from "views/SUPerBuildingUserData";
import { baseUrl } from "api/baseQuery";
import { getAuthHeaders } from "utils/authHeaders";
import { KPI_BETA_SITES } from "config/kpiDashboardConfig";


const AccessRoute = ({ sidebarKey, userRoles, children }) => {
  if (!canAccessSidebarKey(userRoles, sidebarKey, GROUP_MATRIX)) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">You do not have permission to access this page. Please contact your administrator.</Alert>
      </Box>
    );
  }
  return children;
};

const drawerWidth = 270;


const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});


const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));


const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
  background: "#0A2240",
  height: "3.5rem",
  [theme.breakpoints.down('lg')]: {
    marginLeft: 0,
    width: '100%',
  },
}));


const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    background: "#005AA6",
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
    '& .MuiDrawer-paper': {
      backgroundColor: '#005AA6',
    },
  }),
);


const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  minHeight: '3.5rem !important',
  height: '3.5rem',
  display: 'flex',
  alignItems: 'center',
}));


const StyledReorderIcon = styled(ReorderIcon, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ open, theme }) => ({
  fontSize: '2rem',
  marginRight: '1rem',
  cursor: 'pointer',
  display: open ? 'none' : 'block',
  transition: 'opacity 0.3s ease',
  '@media (max-width: 1279px)': {
    display: 'block',
  },
}));


const StyledLogo = styled('img')(({ envName }) => ({
  cursor: 'pointer',
  marginRight: '1.5rem',
  // marginLeft: '1.4rem',
  width: envName !== 'PROD' ? '7rem' : '4rem',
  display: 'block',
  marginBottom: "0.6rem"
}));


export default function App() {
  const theme = useTheme();  
  const [open, setOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [logoFile, setLogoFile] = React.useState(logo);
  const [envName, setEnv] = React.useState('PROD');
  const [selectedItem, setSelectedItem] = React.useState('Zabbix');
  const { keycloak } = useContext(KeycloakContext);
  // const userRole = "Admin"; // Replace with role detection logic
  const [userRoles, setUserRole] = React.useState([]);
  const [isHovering, setIsHovering] = React.useState(false);
  const [addGroup] = useAddGroupMutation();
  const [customerSidebarConfig, setCustomerSidebarConfig] = React.useState(null);
  const wasDrawerOpenBeforeEditingRef = React.useRef(false);

  const [isSmallOrMediumScreen, setIsSmallOrMediumScreen] = React.useState(window.innerWidth < 1280);


  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallOrMediumScreen(window.innerWidth < 1280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  React.useEffect(() => {
    // // comment below code for local run
    const userRole = extractRealmRoles(keycloak.tokenParsed)
    setUserRole(userRole);
    if (!keycloak?.tokenParsed) return;

    const tokenData = keycloak.tokenParsed;

    const payload = {
      name: tokenData?.name,
      username: tokenData?.preferred_username,
      email: tokenData?.email,
      groups: userRole,
    };

    addGroup({ body: payload })
      .unwrap()
      .then(response => {
        console.log("Group mapping response:", response);
      })
      .catch(error => {
        console.error("Group mapping error:", error);
      });
    // console.log("userrole", userRole, JSON.stringify(keycloak));

  }, [keycloak, addGroup])



  React.useEffect(() => {
    const env = 'PROD';
    setEnv(env);
    setLogoFile(logo);
  }, []);

  const handleMouseEnter = () => {
    if (!isSmallOrMediumScreen && !open) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isSmallOrMediumScreen && !open) {
      setIsHovering(false);
    }
  };

  const handleDrawerToggle = () => {
    if (isSmallOrMediumScreen) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(prevOpen => !prevOpen);
    }
  };


  // Get *allowed* sidebar/menu and feature structure for these roles
  const baseSidebarConfig = React.useMemo(
    () => getSidebarForRoles(userRoles, SIDEBAR_MENU, GROUP_MATRIX),
    [userRoles]
  );
  const featuresAllowed = React.useMemo(
    () => getFeaturesForRoles(userRoles, FEATURE_REGISTRY, GROUP_MATRIX),
    [userRoles]
  );

  React.useEffect(() => {
    const internalGroups = ['admin', 'NOC', 'Manager', 'VP', 'Executive', 'Construction', 'Dashboard', 'C-Suite'];
    const isInternalUser = userRoles.some(role => internalGroups.includes(role));
    const customerGroup = userRoles.includes('T-Mobile')
      ? 'T-Mobile'
      : userRoles.includes('Pierson')
      ? 'Pierson'
      : userRoles.includes('AT&T')
      ? 'AT&T'
      : null;

    if (!Array.isArray(baseSidebarConfig) || !customerGroup || isInternalUser) {
      setCustomerSidebarConfig(null);
      return;
    }

    const filenameMatchesCustomer = (filename, group) => {
      const name = (filename || '').toLowerCase();
      if (group === 'T-Mobile') return /(t-mobile|tmobile|t-mo|tmo)/i.test(name);
      if (group === 'AT&T') return /(at&t|\batt\b)/i.test(name);
      if (group === 'Pierson') return /pierson/i.test(name);
      return false;
    };

    const pruneSidebarByAvailability = (menuItems, availableKeys) => {
      return menuItems.reduce((acc, item) => {
        if (['kpi_su', 'kpi_caesars', 'kpi_foa', 'kpi_mocn_foa', 'kpi_tmobile_monthly', 'kpi_windsor'].includes(item.key) && !availableKeys.has(item.key)) {
          return acc;
        }

        if (item.children && Array.isArray(item.children)) {
          const nextChildren = item.children.filter(
            child => !['kpi_su', 'kpi_caesars', 'kpi_foa', 'kpi_mocn_foa', 'kpi_tmobile_monthly', 'kpi_windsor'].includes(child.key) || availableKeys.has(child.key)
          );
          if (item.key === 'kpi_reports' && nextChildren.length === 0) {
            return acc;
          }
          acc.push({ ...item, children: nextChildren });
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);
    };

    let isActive = true;
    const fetchSiteAvailability = async () => {
      try {
        // Prevent temporary flash of all KPI site links while availability is loading.
        setCustomerSidebarConfig(pruneSidebarByAvailability(baseSidebarConfig, new Set()));

        const headers = await getAuthHeaders({ Accept: 'application/json' });
        const siteMap = [
          { key: 'kpi_su', folder: 'su' },
          { key: 'kpi_caesars', folder: 'caesars' },
          { key: 'kpi_foa', folder: 'foa' },
          { key: 'kpi_mocn_foa', folder: '5gfactory' },
          { key: 'kpi_tmobile_monthly', folder: 'tmo-combined' },
          { key: 'kpi_windsor', folder: 'windsor' },
        ];

        const availability = await Promise.all(
          siteMap.map(async ({ key, folder }) => {
            try {
              const res = await fetch(`${baseUrl}/listPdfFolder/?folder=${encodeURIComponent(folder)}`, { headers });
              if (!res.ok) return { key, hasFiles: false };
              const data = await res.json();
              const files = data.files || [];
              const hasFiles = files.some(file => filenameMatchesCustomer(file.filename, customerGroup));
              return { key, hasFiles };
            } catch (err) {
              return { key, hasFiles: false };
            }
          })
        );

        if (!isActive) return;

        const availableKeys = new Set(
          availability.filter(entry => entry.hasFiles).map(entry => entry.key)
        );

        setCustomerSidebarConfig(pruneSidebarByAvailability(baseSidebarConfig, availableKeys));
      } catch (err) {
        if (isActive) {
          setCustomerSidebarConfig(baseSidebarConfig);
        }
      }
    };

    fetchSiteAvailability();
    return () => {
      isActive = false;
    };
  }, [userRoles, baseSidebarConfig]);

  const sidebarConfig = customerSidebarConfig || baseSidebarConfig;


  // Handle special-case redirects
  if (sidebarConfig && sidebarConfig.type === "redirect") {
    window.location.href = sidebarConfig.url;
    return null;
  }


  // Determine home route based on role
  const homeRoute = userRoles.includes('T-Mobile')
    ? '/reports/tmobile'
    : userRoles.includes('Pierson')
    ? '/reports/windsor'
    : userRoles.includes('AT&T')
    ? '/reports/att'
    : '/';

  const handleMenuClick = (text) => {
    setSelectedItem(text);
    if (isSmallOrMediumScreen) {
      setMobileOpen(false);
    }
  };


  const onNavigate = (route) => {
    window.location.hash = route;
  }

  // Sidebar checkboxes are only rendered when the drawer is expanded.
  // The mobile drawer is modal and would cover the header save button, so it is left to the user.
  const handleStartLayoutEditing = () => {
    wasDrawerOpenBeforeEditingRef.current = open;
    if (!isSmallOrMediumScreen) {
      setOpen(true);
    }
  };

  // The user finishes from the header, outside the drawer, so restore its previous state.
  const handleStopLayoutEditing = () => {
    if (!isSmallOrMediumScreen) {
      setOpen(wasDrawerOpenBeforeEditingRef.current);
      setIsHovering(false);
    }
  };


  return (
    <LayoutPreferencesProvider
      sidebarMenu={sidebarConfig}
      features={featuresAllowed}
      onStartEditing={handleStartLayoutEditing}
      onStopEditing={handleStopLayoutEditing}
    >
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <StyledToolbar>
          <StyledReorderIcon open={open} onClick={handleDrawerToggle} />
          <StyledLogo
            src={logoFile}
            alt="Logo"
            loading="lazy"
            envName={envName}
            onClick={() => onNavigate("/")}
          />
          {/* <StyledDivider orientation="vertical" variant="middle" flexItem />
          <StyledTypography
            text="NOC Monitoring"
            noWrap
            component="div"
          /> */}
          <Box sx={{ flexGrow: 1 }} />
          <UserMenu
          />
        </StyledToolbar>
      </AppBar>

      {/* Large Screen Drawer - permanent variant */}
      {!isSmallOrMediumScreen && (
        <Drawer
          variant="permanent"
          open={open || isHovering}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerToggle}>
              {theme.direction === 'rtl' ?
                <ChevronRightIcon sx={{ color: '#ffffff' }} /> :
                <ChevronLeftIcon sx={{ color: '#ffffff' }} />
              }
            </IconButton>
          </DrawerHeader>
          <Sidebar
            menuData={sidebarConfig}
            open={open || isHovering}
            selected={selectedItem}
            onClick={setSelectedItem}
            isSmallOrMediumScreen={isSmallOrMediumScreen}
            onNavigate={onNavigate}
            homeRoute={homeRoute}
          />
        </Drawer>
      )}


      {/* Small/Medium Screen Drawer - temporary variant */}
      {isSmallOrMediumScreen && (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#005AA6',
            },
          }}
        >
          <DrawerHeader>
            <IconButton onClick={() => setMobileOpen(false)}>
              {theme.direction === 'rtl' ?
                <ChevronRightIcon sx={{ color: '#ffffff' }} /> :
                <ChevronLeftIcon sx={{ color: '#ffffff' }} />
              }
            </IconButton>
          </DrawerHeader>
          <Sidebar menuData={sidebarConfig}
            open={true}
            selected={selectedItem}
            onClick={handleMenuClick}
            isSmallOrMediumScreen={isSmallOrMediumScreen}
            onNavigate={onNavigate}
            homeRoute={homeRoute} />
        </MuiDrawer>
      )}


      <Box
        component="main"
        sx={{ flexGrow: 1, backgroundColor: "#F4F7FE", width: "100%" }}
      >
        <DrawerHeader />
        <HashRouter>
          <Routes>
            <Route
              path="/"
              element={

                <Dashboard
                  user={{ role: userRoles }}
                  features={featuresAllowed}
                />


              }
            />

            {/* New XRAN route */}
            <Route path="/xran" element={
              <AccessRoute sidebarKey="xran" userRoles={userRoles}>
                <XranScreen user={{ role: userRoles }} features={featuresAllowed} />
              </AccessRoute>
            } />

            {/* Shift Management route */}
            <Route 
              path="/shifts" 
              element={
                <ProtectedRoute
                  component={ShiftsManagement}
                  currentUser={{
                    id: keycloak?.tokenParsed?.sub,
                    username: keycloak?.tokenParsed?.preferred_username,
                    groups: keycloak?.tokenParsed?.realm_access?.roles || [],
                  }}
                  requiresNOC={true}
                />
              }
            />
            {/* Login Tracking route */}
            <Route path="/login-tracking" element={
              <AccessRoute sidebarKey="userlogin" userRoles={userRoles}>
                <LoginTracking user={{ role: userRoles }} features={featuresAllowed} />
              </AccessRoute>
            } />

            {/* Data Request route */}
            <Route path="/data-request" element={
              <AccessRoute sidebarKey="dataRequest" userRoles={userRoles}>
                <DataRequest user={{ role: userRoles }} features={featuresAllowed} />
              </AccessRoute>
            } />

            {/* Outage Tracker route */}
            <Route path="/outage-tracker" element={
              <AccessRoute sidebarKey="outageTracker" userRoles={userRoles}>
                <OutageTracker user={{ role: userRoles }} features={featuresAllowed} />
              </AccessRoute>
            } />

            {/* NOC Hardware route */}
            <Route path="/noc-hardware" element={
              <AccessRoute sidebarKey="nocHardware" userRoles={userRoles}>
                <NocHardware user={{ role: userRoles }} features={featuresAllowed} />
              </AccessRoute>
            } />

            {/* Alarm Reports Routes */}
            <Route path="/reports/alarm-su" element={
              <AccessRoute sidebarKey="su_alarm_reports" userRoles={userRoles}>
                <AlarmReportsSu />
              </AccessRoute>
            } />
            <Route path="/reports/alarm-all" element={
              <AccessRoute sidebarKey="all_sites_alarm_reports" userRoles={userRoles}>
                <AlarmReportsAll />
              </AccessRoute>
            } />
            <Route path="/reports/alarm-intl" element={
              <AccessRoute sidebarKey="international_alarm_reports" userRoles={userRoles}>
                <AlarmReportsIntl />
              </AccessRoute>
            } />

            {/* Legacy KPI pages – hidden from sidebar, accessible via direct link */}
            <Route path="/reports/windsor" element={<WindsorReports />} />
            <Route path="/reports/tmobile" element={<TmobileReports />} />

            {/* New KPI site pages */}
            <Route path="/reports/site/su" element={
              <AccessRoute sidebarKey="kpi_su" userRoles={userRoles}>
                <SiteReports folder="su" title="Syracuse University" sidebarKey="kpi_su" />
              </AccessRoute>
            } />
            <Route path="/reports/site/caesars" element={
              <AccessRoute sidebarKey="kpi_caesars" userRoles={userRoles}>
                <SiteReports folder="caesars" title="Caesars NOBU" sidebarKey="kpi_caesars" />
              </AccessRoute>
            } />
            <Route path="/reports/site/foa" element={
              <AccessRoute sidebarKey="kpi_foa" userRoles={userRoles}>
                <SiteReports folder="foa" title="IPSec FOA" sidebarKey="kpi_foa" />
              </AccessRoute>
            } />
            <Route path="/reports/site/5gfactory" element={
              <AccessRoute sidebarKey="kpi_mocn_foa" userRoles={userRoles}>
                <SiteReports folder="5gfactory" title="MOCN FOA" sidebarKey="kpi_mocn_foa" />
              </AccessRoute>
            } />
            <Route path="/reports/site/tmo-combined" element={
              <AccessRoute sidebarKey="kpi_tmobile_monthly" userRoles={userRoles}>
                <SiteReports folder="tmo-combined" title="T-Mobile Monthly Reports" sidebarKey="kpi_tmobile_monthly" />
              </AccessRoute>
            } />
            <Route path="/reports/site/windsor" element={
              <AccessRoute sidebarKey="kpi_windsor" userRoles={userRoles}>
                <SiteReports folder="windsor" title="Windsor" sidebarKey="kpi_windsor" />
              </AccessRoute>
            } />

            {KPI_BETA_SITES.map((site) => (
              <Route
                key={site.key}
                path={`/reports/beta/${site.key}`}
                element={
                  <AccessRoute sidebarKey={site.sidebarKey} userRoles={userRoles}>
                    <KpiDashboardBeta siteKey={site.key} title={site.title} />
                  </AccessRoute>
                }
              />
            ))}

            {/* XRAN Tracking route */}
            <Route path="/xran-tracking" element={
              <AccessRoute sidebarKey="xran" userRoles={userRoles}>
                <XranTracking
                  user={{
                    role: userRoles,
                    username: keycloak?.tokenParsed?.preferred_username,
                    sub: keycloak?.tokenParsed?.sub,
                  }}
                  features={featuresAllowed}
                />
              </AccessRoute>
            } />

            {/* xRAN Certificates route */}
            <Route path="/xran-certs" element={
              <AccessRoute sidebarKey="xranCerts" userRoles={userRoles}>
                <XranCerts />
              </AccessRoute>
            } />

            {/* Release Notes route — admin only */}
            <Route path="/release-notes" element={
              <AccessRoute sidebarKey="releaseNotes" userRoles={userRoles}>
                <ReleaseNotes />
              </AccessRoute>
            } />

            {/* Campus Map route */}
            <Route path="/campus-map" element={
              <AccessRoute sidebarKey="campusMap" userRoles={userRoles}>
                <CampusMap />
              </AccessRoute>
            } />

            <Route path="/su-building-data" element={
              <AccessRoute sidebarKey="suBuildingData" userRoles={userRoles}>
                <SUPerBuildingUserData />
              </AccessRoute>
            } />

          </Routes>
        </HashRouter>
      </Box>
    </Box>
    </LayoutPreferencesProvider>
  );
}
